var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Mixin } from "../../../../ChronoGraph/class/BetterMixin.js";
import { calculate, field } from "../../../../ChronoGraph/replica/Entity.js";
import { model_field } from "../../../chrono/ModelFieldAtom.js";
import { HasChildrenMixin } from "../scheduler_basic/HasChildrenMixin.js";
/**
 * This is a mixin, adding critical path calculation to the event node.
 *
 * Scheduling-wise it adds *criticalPaths* field to an entity mixing it.
 *
 * For more details on the _critical path method_ please check this article: https://en.wikipedia.org/wiki/Critical_path_method
 */
export class HasCriticalPathsMixin extends Mixin([HasChildrenMixin], (base) => {
    class HasCriticalPathsMixin extends base {
        constructor() {
            super(...arguments);
            /**
             * The maximum number of _critical paths_ to collect in [[criticalPaths]].
             * When critical tasks connectivity is high (there are many of critical tasks having many
             * critical predecessors) the tasks might produce a huge number of critical paths
             * which can't really be handled by a browser.
             * So this value limits the number of paths to collect to protect from such cases.
             */
            this.maxCriticalPathsCount = 10000;
        }
        *getEventCriticalPredecessors(event, cache) {
            if (cache.has(event))
                return cache.get(event);
            const result = [];
            const dependencies = yield event.$.incomingDeps;
            const { parent } = event;
            // collect critical predecessors
            for (const dependency of dependencies) {
                const event = yield dependency.$.fromEvent;
                // if we found a critical predecessor
                if (event && (yield dependency.$.active) && !(yield event.$.inactive) && (yield event.$.critical)) {
                    result.push({ event, dependency });
                }
            }
            if (parent) {
                result.push(...(yield* this.getEventCriticalPredecessors(parent, cache)));
            }
            cache.set(event, result);
            return result;
        }
        *calculateCriticalPaths() {
            const paths = [], pathsToProcess = [], events = yield this.$.childEvents, eventsToProcess = [...events], projectEndDate = yield this.$.endDate, maxCriticalPathsCount = yield this.$.maxCriticalPathsCount;
            // First collect events we'll start collecting paths from.
            // We need to start from critical events w/o incoming dependencies
            let event;
            while ((event = eventsToProcess.shift())) {
                const childEvents = yield event.$.childEvents, eventIsCritical = yield event.$.critical, eventIsActive = !(yield event.$.inactive), eventEndDate = yield event.$.endDate;
                // register a new path finishing at the event
                if (eventIsActive && eventEndDate && eventEndDate.getTime() - projectEndDate.getTime() === 0 && eventIsCritical) {
                    pathsToProcess.push([{ event }]);
                }
                eventsToProcess.push(...childEvents);
            }
            let path;
            const eventPredecessors = new Map();
            // fetch paths one by one and process
            while ((path = pathsToProcess.shift())) {
                let taskIndex = path.length - 1, node;
                // get the path last event
                while ((node = path[taskIndex])) {
                    const criticalPredecessorNodes = eventPredecessors.has(node.event)
                        ? eventPredecessors.get(node.event) : yield* this.getEventCriticalPredecessors(node.event, eventPredecessors);
                    // if critical predecessor(s) found
                    if (criticalPredecessorNodes.length) {
                        // if we found more than one predecessor we start new path as: leading path + predecessor
                        for (let i = 1; i < criticalPredecessorNodes.length; i++) {
                            pathsToProcess.push(path.concat(criticalPredecessorNodes[i]));
                        }
                        // append the found predecessor to the path
                        path.push(criticalPredecessorNodes[0]);
                        // increment counter to process the predecessor we've appended to the path
                        taskIndex++;
                    }
                    else {
                        // no predecessors -> stop the loop
                        taskIndex = -1;
                    }
                }
                // we collected the path backwards so let's reverse it
                paths.push(path.reverse());
                // Stop if reached the maximum number of paths
                if (paths.length === maxCriticalPathsCount)
                    break;
            }
            return paths;
        }
    }
    __decorate([
        field({ lazy: true })
    ], HasCriticalPathsMixin.prototype, "criticalPaths", void 0);
    __decorate([
        model_field({ type: 'number', persist: false })
    ], HasCriticalPathsMixin.prototype, "maxCriticalPathsCount", void 0);
    __decorate([
        calculate('criticalPaths')
    ], HasCriticalPathsMixin.prototype, "calculateCriticalPaths", null);
    return HasCriticalPathsMixin;
}) {
}
